import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useCreateAnnouncementMutation } from '../adminTools.mutations';
import { adminToolsKeys, fetchOrganizationUsers } from '../adminTools.queries';
import type {
  CreateAnnouncementResponse,
  UserWithOrganization,
  OrganizationUser,
  OrganizationsResponse,
} from '../../../types/api';

type AnnouncementTargetType = 'all_users' | 'organizations' | 'users';

interface UseAnnouncementsArgs {
  /** Shared org list (owned by the parent, also used by org management). */
  orgsData: OrganizationsResponse | null;
  /** Refetch the shared org list when it hasn't resolved yet on first open. */
  refetchOrgs: () => Promise<OrganizationsResponse | null>;
}

export interface UseAnnouncementsReturn {
  dialogOpen: boolean;
  availableUsers: UserWithOrganization[];
  // Form state
  title: string;
  body: string;
  files: File[];
  targetType: AnnouncementTargetType;
  selectedOrgs: string[];
  selectedUsers: string[];
  sendEmailNotification: boolean;
  // Result state
  sending: boolean;
  sendResult: CreateAnnouncementResponse | null;
  sendError: string;
  // Actions
  openDialog: () => Promise<void>;
  closeDialog: () => void;
  send: () => Promise<void>;
  handleFilesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (index: number) => void;
  // Setters
  setTitle: (value: string) => void;
  setBody: (value: string) => void;
  setTargetType: (value: AnnouncementTargetType) => void;
  setSelectedOrgs: (value: string[]) => void;
  setSelectedUsers: (value: string[]) => void;
  setSendEmailNotification: (value: boolean) => void;
}

export function useAnnouncements({
  orgsData,
  refetchOrgs,
}: UseAnnouncementsArgs): UseAnnouncementsReturn {
  const { showError, showSuccess } = useErrorHandler();
  const queryClient = useQueryClient();
  const createAnnouncementMutation = useCreateAnnouncementMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendResult, setSendResult] = useState<CreateAnnouncementResponse | null>(null);
  const [sendError, setSendError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [targetType, setTargetType] = useState<AnnouncementTargetType>('all_users');
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserWithOrganization[]>([]);
  const [sendEmailNotification, setSendEmailNotification] = useState(false);

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setSendResult(null);
    setSendError('');

    // Populate availableUsers — fetch all orgs' users for per-user targeting.
    // orgsData is normally already cached; refetch if the dialog was opened before
    // the initial query resolved (race on first page load).
    const resolvedOrgsData = orgsData ?? (await refetchOrgs());
    if (resolvedOrgsData) {
      const allUsers: UserWithOrganization[] = [];
      for (const org of resolvedOrgsData.organizations) {
        try {
          const orgUsers = await queryClient.fetchQuery({
            queryKey: adminToolsKeys.organizationUsers(org.id),
            queryFn: () => fetchOrganizationUsers(org.id),
          });
          allUsers.push(
            ...orgUsers.users.map(
              (u: OrganizationUser): UserWithOrganization => ({
                ...u,
                organization_name: org.name,
                organization_id: org.id,
              })
            )
          );
        } catch (error) {
          console.error(`Error fetching users for org ${org.name}:`, error);
        }
      }
      setAvailableUsers(allUsers);
    }
  }, [orgsData, refetchOrgs, queryClient]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setSendResult(null);
    setSendError('');
    setTitle('');
    setBody('');
    setFiles([]);
    setTargetType('all_users');
    setSelectedOrgs([]);
    setSelectedUsers([]);
    setAvailableUsers([]);
    setSendEmailNotification(false);
  }, []);

  const handleFilesChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    setFiles(selected);
    setSendError('');
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const send = useCallback(async () => {
    if (!title.trim()) {
      setSendError('Title is required');
      return;
    }
    if (!body.trim()) {
      setSendError('Message body is required');
      return;
    }

    if (targetType === 'organizations' && selectedOrgs.length === 0) {
      setSendError('Please select at least one organization');
      return;
    }
    if (targetType === 'users' && selectedUsers.length === 0) {
      setSendError('Please select at least one user');
      return;
    }

    setSendResult(null);
    setSendError('');

    try {
      const targetIds =
        targetType === 'all_users'
          ? []
          : targetType === 'organizations'
            ? selectedOrgs
            : selectedUsers;

      const result = await createAnnouncementMutation.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        targetType,
        targetIds,
        files,
        sendEmailNotification,
      });

      setSendResult(result);

      setTitle('');
      setBody('');
      setFiles([]);
      setTargetType('all_users');
      setSendEmailNotification(false);
      setSelectedOrgs([]);
      setSelectedUsers([]);

      setTimeout(() => setSendResult(null), 5000);
      showSuccess('Announcement sent successfully');
    } catch (error) {
      showError(error, 'Failed to send announcement');
    }
  }, [
    title,
    body,
    targetType,
    selectedOrgs,
    selectedUsers,
    files,
    sendEmailNotification,
    createAnnouncementMutation,
    showError,
    showSuccess,
  ]);

  return {
    dialogOpen,
    availableUsers,
    title,
    body,
    files,
    targetType,
    selectedOrgs,
    selectedUsers,
    sendEmailNotification,
    sending: createAnnouncementMutation.isPending,
    sendResult,
    sendError,
    openDialog,
    closeDialog,
    send,
    handleFilesChange,
    removeFile,
    setTitle,
    setBody,
    setTargetType,
    setSelectedOrgs,
    setSelectedUsers,
    setSendEmailNotification,
  };
}
